// src/pages/personal/analytics/CustomerSection/CustomerSection.tsx
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Flex, Grid } from '@/components/ui/Layout';
import { TEXT_STYLES } from '@/theme';
import CustomerMetrics from './components/CustomerMetrics';
import CustomerSegmentation from './components/CustomerSegmentation';
import LeadSourceDistribution from './components/LeadSourceDistribution';
import LtvAnalysis from './components/LtvAnalysis';
import TopClients from './components/TopClients';
import CustomerFilters from './components/CustomerFilters';
import { PeriodSelector } from '@/components/ui/PeriodSelector';
import { useCustomerData } from './hooks/useCustomerData';
import { Period, DateRange } from '../types/types';
// Импортируем утилиту для форматирования дат
import { formatSmartDate } from '@/utils/dateUtils';

import styles from './CustomerSection.module.css';

const CustomerSection: React.FC = () => {
  const { t, i18n } = useTranslation('customer');
  const DEBUG = process.env.NODE_ENV === 'development';
  const currentLocale = i18n.language;
  
  // Состояния для периода и фильтров
  const [period, setPeriod] = useState<Period>('month');
  const [statuses, setStatuses] = useState<string[]>([]);
  const [leadSources, setLeadSources] = useState<string[]>([]);
  const [dateRange, setDateRangeState] = useState<DateRange | undefined>();
  
  // Эффект для установки начального периода при монтировании компонента
  useEffect(() => {
    // Устанавливаем период "month" с актуальным диапазоном дат при первом рендере
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    
    const initialDateRange = {
      startDate: new Date(year, month, 1),
      endDate: new Date(year, month, lastDay)
    };
    
    // Устанавливаем время
    initialDateRange.startDate.setHours(0, 0, 0, 0);
    initialDateRange.endDate.setHours(23, 59, 59, 999);
    
    // Обновляем состояния
    setPeriod('month');
    setDateRangeState(initialDateRange);
  }, []);
  
  const {
    data,
    isLoading,
    error,
    lastUpdated,    
    setDateRange,
    refreshData
  } = useCustomerData({
    period,    
    statuses,
    leadSources,
    dateRange
  });
  
  // Мемоизируем обработчик изменения периода
  const handlePeriodChange = useCallback((newPeriod: Period, newDateRange?: DateRange) => {
    if (DEBUG) console.log('CustomerSection - Изменение периода:', newPeriod, newDateRange);
    
    setPeriod(newPeriod);
    
    if (newDateRange) {
      // Проверяем валидность dateRange перед обновлением
      if (newDateRange.startDate instanceof Date && 
          newDateRange.endDate instanceof Date && 
          !isNaN(newDateRange.startDate.getTime()) && 
          !isNaN(newDateRange.endDate.getTime())) {
        setDateRangeState(newDateRange);
        setDateRange(newDateRange);
      }
    }
  }, [DEBUG, setDateRange]);
  
  // Обработчик изменения фильтров
  const handleFiltersChange = useCallback((newStatuses: string[], newLeadSources: string[]) => {
    if (DEBUG) console.log('CustomerSection - Изменение фильтров:', newStatuses, newLeadSources);
    
    setStatuses(newStatuses);
    setLeadSources(newLeadSources);
  }, [DEBUG]);
  
  // Обработчик сброса фильтров
  const handleResetFilters = useCallback(() => {
    if (DEBUG) console.log('CustomerSection - Сброс фильтров');
    
    setStatuses([]);
    setLeadSources([]);
    refreshData();
  }, [DEBUG, refreshData]);
  
  // Функция для форматирования времени последнего обновления
  const getLastUpdatedText = () => {
    if (!lastUpdated) return '';
    
    return `${t('last_updated')}: ${formatSmartDate(lastUpdated, true, currentLocale)}`;
  };
  
  // Определяем, можно ли взаимодействовать с UI
  const canInteract = !isLoading;
  
  // Отображение загрузки
  if (isLoading && !data) {
    return (
      <Container size="full" padding="md">
        <div className={styles.container}>
          <Flex direction="column" gap="md">
            <h1 className={TEXT_STYLES.h1}>{t('title', 'Клиенты')}</h1>
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner}></div>
              <div className={styles.loadingText}>{t('common.loading', 'Загрузка...')}</div>
            </div>
          </Flex>
        </div>
      </Container>
    );
  }
  
  return (
    <Container size="full" padding="lg">
      <div className={styles.container}>
        <Grid cols={1} gap="md">
          <Flex justify="between" align="center" wrap={false}>
            <h1 className={TEXT_STYLES.h1}>{t('title', 'Клиенты')}</h1>
            {lastUpdated && (
              <span className="text-sm text-gray-500">{getLastUpdatedText()}</span>
            )}
          </Flex>
          
          {/* Элементы управления - выровнены по образцу FunnelSection */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginBottom: "1rem", alignItems: "center" }}>
            <PeriodSelector 
              period={period} 
              onChange={handlePeriodChange}
              showTitle={false}  
              defaultPeriodType="byDay"  
              showTodayButton={false}
              buttonLabels={{
                byDay: t('period_labels.byMonth', 'По месяцам'),
                byMonth: t('period_labels.byYear', 'По годам')
              }}          
            />
            
            {/* Компонент фильтров */}
            <CustomerFilters
              statuses={statuses}
              leadSources={leadSources}
              onChange={handleFiltersChange}
              disabled={!canInteract}
              isLoading={isLoading}
              onReset={handleResetFilters}
            />
          </div>
          
          {/* Отображаем часовой пояс бизнеса, если доступен */}
          {data?.timezone && (
            <div className="text-sm text-gray-500 mt-1">
              {t('timezone', 'Часовой пояс')}: {data.timezone}
            </div>
          )}
          
          {/* Отображение ошибки */}
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}
          
          {/* Содержимое - отображаем только если данные загружены */}
          {data && (
            <Grid cols={1} gap="lg">
              {/* Секция с метриками */}
              <div className={styles.chartSection}>
                <CustomerMetrics 
                  data={data?.metrics} 
                  isLoading={isLoading} 
                />
              </div>
              
              {/* Секция с распределением статусов */}
              <div className={styles.chartSection}>
                <LeadSourceDistribution 
                  data={data?.status_distribution || []} 
                  isLoading={isLoading}
                />
              </div>
              
              {/* Секция с сегментацией клиентов */}
              <div className={styles.chartSection}>
                <CustomerSegmentation 
                  data={data?.leadsource_distribution || []} 
                  isLoading={isLoading}
                />
              </div>
              
              {/* Секция с LTV анализом */}
              <div className={styles.chartSection}>
                <LtvAnalysis 
                  data={data?.frequency_by_status || []} 
                  newClientsData={data?.new_clients_trend || []}
                  isLoading={isLoading}
                />
              </div>
              
              {/* Секция с лучшими клиентами - отображаем только если есть данные */}
              {data?.top_clients && data.top_clients.length > 0 && (
                <div className={styles.chartSection}>
                  <TopClients 
                    data={data.top_clients} 
                    isLoading={isLoading} 
                  />
                </div>
              )}
            </Grid>
          )}
        </Grid>
      </div>
    </Container>
  );
};

export default CustomerSection;